import { Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listBankruptcy } from "@/features/transcripts/api";
import { AsyncContent } from "@/features/transcripts/components/async-content";
import { useResource } from "@/features/transcripts/hooks/use-resource";
import { exportToCsv } from "@/features/transcripts/lib/export-csv";
import type { BankruptcyRow, DischargeabilityVerdict } from "@/features/transcripts/types";
import { useTranscriptsContext } from "@/pages/client/transcripts/transcripts-layout";
import { cn } from "@/lib/utils";

const verdictClassName: Record<DischargeabilityVerdict, string> = {
  Dischargeable: "border-success/20 bg-success/10 text-success",
  "Not Dischargeable": "border-destructive/20 bg-destructive/10 text-destructive",
  "Review Needed": "border-warning/20 bg-warning/10 text-warning",
};

export default function ClientBankruptcyPage() {
  const { client, forWho, dataVersion } = useTranscriptsContext();
  const state = useResource<BankruptcyRow[]>(
    () => listBankruptcy(client.id, forWho),
    [client.id, forWho, dataVersion],
  );

  function handleExport(rows: BankruptcyRow[]) {
    exportToCsv(`${client.id}-bankruptcy-analysis.csv`, rows, [
      { header: "Tax Year", accessor: (r) => r.taxYear },
      { header: "Form", accessor: (r) => r.form },
      { header: "3-Year Rule", accessor: (r) => `${r.threeYearRuleDate ?? "—"} (${r.threeYearRuleMet ? "Met" : "Not Met"})` },
      { header: "2-Year Rule", accessor: (r) => (r.twoYearRuleDate ? `${r.twoYearRuleDate} (${r.twoYearRuleMet ? "Met" : "Not Met"})` : "N/A") },
      { header: "240-Day Rule", accessor: (r) => (r.twoFourZeroDayRuleDate ? `${r.twoFourZeroDayRuleDate} (${r.twoFourZeroDayRuleMet ? "Met" : "Not Met"})` : "N/A") },
      { header: "Lien Survives", accessor: (r) => (r.lienSurvivesFlag ? "Yes" : "No") },
      { header: "Late Filed", accessor: (r) => (r.lateFiledFlag ? "Yes" : "No") },
      { header: "Verdict", accessor: (r) => r.verdict },
    ]);
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-foreground mb-2 text-3xl font-bold">Bankruptcy Dischargeability Analysis</h1>
        <p className="text-muted-foreground max-w-2xl">
          The 3-year, 2-year, and 240-day IRC §507(a)(8) tests per tax year. Not a substitute
          for attorney review.
        </p>
      </div>

      <AsyncContent state={state}>
        {(rows) => (
          <div className="flex flex-col gap-4">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => handleExport(rows)} disabled={rows.length === 0}>
                <Download /> Export CSV
              </Button>
            </div>
            <Card className="gap-0 overflow-hidden p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Tax Year</TableHead>
                    <TableHead>Form</TableHead>
                    <TableHead>3-Year Rule</TableHead>
                    <TableHead>2-Year Rule</TableHead>
                    <TableHead>240-Day Rule</TableHead>
                    <TableHead>Lien Survives</TableHead>
                    <TableHead>Verdict</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={`${row.taxYear}-${row.form}`}>
                      <TableCell className="font-medium">{row.taxYear}</TableCell>
                      <TableCell>{row.form}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.threeYearRuleDate ?? "—"} {row.threeYearRuleMet ? "✓" : "✗"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.twoYearRuleDate ? `${row.twoYearRuleDate} ${row.twoYearRuleMet ? "✓" : "✗"}` : "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.twoFourZeroDayRuleDate
                          ? `${row.twoFourZeroDayRuleDate} ${row.twoFourZeroDayRuleMet ? "✓" : "✗"}`
                          : "—"}
                      </TableCell>
                      <TableCell>{row.lienSurvivesFlag ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn("text-[11px] font-bold uppercase", verdictClassName[row.verdict])}>
                          {row.verdict}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-muted-foreground text-center">
                        No tax records for this selection.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}
      </AsyncContent>
    </div>
  );
}
